import { useState } from 'react';

export default function HomePage() {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    message: ''
  });
  const [formStatus, setFormStatus] = useState<'idle' | 'submitting' | 'success' | 'error'>('idle');

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setFormStatus('submitting');

    try {
      const formBody = new URLSearchParams();
      formBody.append('name', formData.name);
      formBody.append('email', formData.email);
      formBody.append('message', formData.message);

      const response = await fetch('https://readdy.ai/api/form/d6g4r11ghdq4qda701qg', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/x-www-form-urlencoded',
        },
        body: formBody.toString()
      });

      if (response.ok) {
        setFormStatus('success');
        setFormData({ name: '', email: '', message: '' });
        setTimeout(() => setFormStatus('idle'), 3000);
      } else {
        setFormStatus('error');
        setTimeout(() => setFormStatus('idle'), 3000);
      }
    } catch (error) {
      setFormStatus('error');
      setTimeout(() => setFormStatus('idle'), 3000);
    }
  };

  return (
    <div className="min-h-screen bg-white">
      {/* Navigation */}
      <nav className="fixed top-0 left-0 right-0 z-50 transition-all duration-300 bg-white/95 backdrop-blur-sm shadow-sm">
        <div className="max-w-7xl mx-auto px-6 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <img 
                src="https://public.readdy.ai/ai/img_res/56091114-98dd-472c-91c1-3df9f5a8ef47.png" 
                alt="CHAMPION d.o.o. Logo" 
                className="h-10 w-10 object-contain"
              />
              <h1 className="text-xl font-semibold text-gray-900">CHAMPION d.o.o.</h1>
            </div>
            <div className="flex items-center gap-8">
              <a href="#home" className="text-sm font-medium text-gray-700 hover:text-teal-500 transition-colors cursor-pointer">Home</a>
              <a href="#about" className="text-sm font-medium text-gray-700 hover:text-teal-500 transition-colors cursor-pointer">About</a>
              <a href="#products" className="text-sm font-medium text-gray-700 hover:text-teal-500 transition-colors cursor-pointer">Products</a>
              <a href="#features" className="text-sm font-medium text-gray-700 hover:text-teal-500 transition-colors cursor-pointer">Features</a>
              <a href="#contact" className="text-sm font-medium text-gray-700 hover:text-teal-500 transition-colors cursor-pointer">Contact</a>
            </div>
          </div>
        </div>
      </nav>

      {/* Hero Section */}
      <section id="home" className="relative min-h-screen flex items-center pt-20">
        <div className="absolute inset-0 bg-gradient-to-br from-sky-100 via-purple-50 to-purple-100"></div>
        <div className="absolute inset-0 bg-gradient-to-b from-black/5 via-transparent to-black/10"></div>
        
        <div className="relative max-w-7xl mx-auto px-6 py-20 w-full">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            <div>
              <div className="inline-flex items-center gap-2 bg-white px-4 py-2 rounded-full shadow-sm mb-8">
                <i className="ri-apple-fill text-gray-700 text-lg"></i>
                <span className="text-sm font-medium text-gray-700">iOS Development Experts</span>
              </div>
              
              <h2 className="text-6xl font-bold text-gray-900 leading-tight mb-6">
                Modern Applications<br />
                For Enhanced<br />
                Mobile Experience
              </h2>
              
              <div className="space-y-4 text-lg text-gray-600 leading-relaxed mt-12">
                <p>
                  We are a team of experts developing modern applications for the iOS platform. Our products are designed to enhance user experience and improve the security of mobile devices.
                </p>
                <p>
                  Specializing in VPN and Cleaner solutions, we deliver powerful tools that protect your privacy and optimize your device performance.
                </p>
              </div>
            </div>
            
            <div className="flex justify-center">
              <div className="w-full max-w-lg">
                <img 
                  src="https://public.readdy.ai/ai/img_res/90d0825897d4eb4fadad069216412d22.jpg"
                  alt="iOS App Mockups"
                  className="w-full h-auto rounded-3xl shadow-2xl object-cover"
                />
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* About Section */}
      <section id="about" className="py-24 bg-white">
        <div className="max-w-7xl mx-auto px-6">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-center">
            <div>
              <p className="text-2xl text-gray-800 leading-relaxed mb-8">
                CHAMPION d.o.o. is dedicated to creating innovative iOS applications that make a real difference in users' daily lives. Our expertise spans security, performance optimization, and user experience design.
              </p>
              
              <div className="inline-flex items-center gap-2 bg-teal-50 px-5 py-3 rounded-full">
                <span className="text-sm font-medium text-gray-800">iOS Development Excellence</span>
                <i className="ri-arrow-right-line text-gray-800"></i>
              </div>
            </div>
            
            <div className="relative">
              <div className="rounded-3xl overflow-hidden shadow-2xl">
                <div className="relative">
                  <img 
                    src="https://readdy.ai/api/search-image?query=modern-tech-office-workspace-with-ios-developers-working-on-macbooks-and-iphones-bright-natural-lighting-clean-minimalist-interior-design-professional-team-environment-croatia-office-setting-contemporary-workspace-photography&width=600&height=750&seq=about-team-001&orientation=portrait"
                    alt="CHAMPION d.o.o. Team"
                    className="w-full h-full object-cover"
                  />
                  <div className="absolute top-6 left-6">
                    <div className="bg-white px-4 py-2 rounded-full shadow-md">
                      <span className="text-sm font-medium text-gray-800">Slatina, Croatia</span>
                    </div>
                  </div>
                  <div className="absolute inset-x-0 bottom-0 bg-gradient-to-t from-black/60 to-transparent p-6">
                    <div className="text-white">
                      <div className="text-lg font-semibold">CHAMPION d.o.o.</div>
                      <div className="text-sm opacity-90">iOS Development Team</div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Products Section */}
      <section id="products" className="py-24 bg-gray-50">
        <div className="max-w-7xl mx-auto px-6">
          <div className="text-center mb-16">
            <h3 className="text-5xl font-bold text-gray-900 mb-2">Our Premium</h3>
            <h3 className="text-5xl font-bold text-teal-500">iOS Applications</h3>
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {/* VPN Product */}
            <div className="bg-white rounded-3xl overflow-hidden shadow-lg hover:shadow-xl transition-shadow">
              <div className="bg-blue-50 h-64 flex items-center justify-center p-8">
                <img 
                  src="https://readdy.ai/api/search-image?query=modern-vpn-app-icon-design-shield-with-lock-symbol-gradient-blue-colors-ios-style-rounded-square-app-icon-clean-minimalist-design-security-theme-professional-3d-render-simple-white-background&width=300&height=300&seq=vpn-icon-001&orientation=squarish"
                  alt="VPN App Icon"
                  className="w-40 h-40 object-contain"
                />
              </div>
              <div className="p-8 text-center">
                <h4 className="text-xl font-semibold text-gray-900 mb-4">SecureVPN</h4>
                <p className="text-sm text-gray-600 mb-4 leading-relaxed">
                  Protect your online privacy with military-grade encryption. Browse securely and anonymously from anywhere in the world.
                </p>
                <p className="text-xs text-gray-400">Enterprise-grade encryption</p>
              </div>
            </div>

            {/* Cleaner Product */}
            <div className="bg-white rounded-3xl overflow-hidden shadow-lg hover:shadow-xl transition-shadow">
              <div className="bg-gray-50 h-64 flex items-center justify-center p-8">
                <img 
                  src="https://readdy.ai/api/search-image?query=modern-cleaner-app-icon-design-broom-or-sparkle-symbol-gradient-gray-silver-colors-ios-style-rounded-square-app-icon-clean-minimalist-design-optimization-theme-professional-3d-render-simple-white-background&width=300&height=300&seq=cleaner-icon-001&orientation=squarish"
                  alt="Cleaner App Icon"
                  className="w-40 h-40 object-contain"
                />
              </div>
              <div className="p-8 text-center">
                <h4 className="text-xl font-semibold text-gray-900 mb-4">SmartCleaner</h4>
                <p className="text-sm text-gray-600 mb-4 leading-relaxed">
                  Free up storage space and optimize your device performance. Remove junk files and duplicate photos with one tap.
                </p>
                <p className="text-xs text-gray-400">Intelligent storage management</p>
              </div>
            </div>

            {/* Performance Product */}
            <div className="bg-white rounded-3xl overflow-hidden shadow-lg hover:shadow-xl transition-shadow">
              <div className="bg-purple-50 h-64 flex items-center justify-center p-8">
                <img 
                  src="https://readdy.ai/api/search-image?query=modern-performance-app-icon-design-speedometer-or-graph-symbol-gradient-purple-lavender-colors-ios-style-rounded-square-app-icon-clean-minimalist-design-speed-optimization-theme-professional-3d-render-simple-white-background&width=300&height=300&seq=performance-icon-001&orientation=squarish"
                  alt="Performance App Icon"
                  className="w-40 h-40 object-contain"
                />
              </div>
              <div className="p-8 text-center">
                <h4 className="text-xl font-semibold text-gray-900 mb-4">SpeedBoost</h4>
                <p className="text-sm text-gray-600 mb-4 leading-relaxed">
                  Enhance your device speed with real-time optimization. Monitor battery health and manage background processes efficiently.
                </p>
                <p className="text-xs text-gray-400">Real-time optimization</p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Features Section */}
      <section id="features" className="py-24 bg-white">
        <div className="max-w-7xl mx-auto px-6">
          <div className="text-center mb-16">
            <h3 className="text-5xl font-bold text-gray-900 mb-2">Powerful Solutions for</h3>
            <h3 className="text-5xl font-bold">
              <span className="text-gray-900">iOS Security &amp; </span>
              <span className="text-teal-500">Performance</span>
            </h3>
          </div>
          
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
            {/* Large Feature Card */}
            <div className="relative rounded-3xl overflow-hidden shadow-xl h-[500px] group cursor-pointer">
              <img 
                src="https://readdy.ai/api/search-image?query=abstract-digital-security-shield-with-vpn-protection-concept-deep-blue-to-purple-gradient-background-glowing-network-connections-cyber-security-visualization-modern-tech-illustration-minimalist-style-professional-digital-art-clean-composition&width=700&height=900&seq=vpn-feature-001&orientation=portrait"
                alt="VPN Protection"
                className="w-full h-full object-cover"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-black/30 to-transparent"></div>
              <div className="absolute inset-0 flex flex-col items-center justify-center">
                <div className="w-20 h-20 flex items-center justify-center mb-6">
                  <i className="ri-shield-check-fill text-white text-6xl"></i>
                </div>
              </div>
              <div className="absolute bottom-8 left-8">
                <div className="bg-black/60 backdrop-blur-sm px-6 py-3 rounded-2xl">
                  <h4 className="text-2xl font-bold text-white">VPN Protection</h4>
                </div>
              </div>
            </div>

            {/* Right Column - Two Stacked Cards */}
            <div className="flex flex-col gap-8">
              {/* Device Optimization */}
              <div className="relative rounded-3xl overflow-hidden shadow-xl h-[236px] group cursor-pointer">
                <img 
                  src="https://readdy.ai/api/search-image?query=abstract-device-cleaning-optimization-concept-soft-mint-green-teal-gradient-background-minimalist-geometric-shapes-storage-management-visualization-modern-tech-illustration-clean-simple-design-professional-digital-art&width=700&height=400&seq=cleaner-feature-001&orientation=landscape"
                  alt="Device Optimization"
                  className="w-full h-full object-cover"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-black/20 to-transparent"></div>
                <div className="absolute inset-0 flex items-center justify-center">
                  <div className="w-16 h-16 flex items-center justify-center">
                    <i className="ri-brush-fill text-white text-5xl"></i>
                  </div>
                </div>
                <div className="absolute bottom-6 right-6">
                  <div className="bg-white/90 backdrop-blur-sm px-5 py-2 rounded-xl">
                    <h4 className="text-lg font-bold text-gray-900">Device Optimization</h4>
                  </div>
                </div>
              </div>

              {/* Speed Enhancement */}
              <div className="relative rounded-3xl overflow-hidden shadow-xl h-[236px] group cursor-pointer">
                <img 
                  src="https://readdy.ai/api/search-image?query=abstract-speed-performance-graph-concept-soft-blush-pink-rose-gradient-background-upward-trending-chart-visualization-modern-tech-illustration-minimalist-geometric-design-professional-digital-art-clean-composition&width=700&height=400&seq=speed-feature-001&orientation=landscape"
                  alt="Speed Enhancement"
                  className="w-full h-full object-cover"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-black/20 to-transparent"></div>
                <div className="absolute inset-0 flex items-center justify-center">
                  <div className="w-16 h-16 flex items-center justify-center">
                    <i className="ri-speed-fill text-white text-5xl"></i>
                  </div>
                </div>
                <div className="absolute bottom-6 left-6">
                  <div className="bg-white/90 backdrop-blur-sm px-5 py-2 rounded-xl">
                    <h4 className="text-lg font-bold text-gray-900">Speed Enhancement</h4>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="relative h-[800px] overflow-hidden">
        <img 
          src="https://readdy.ai/api/search-image?query=modern-minimalist-office-desk-with-multiple-iphones-and-macbook-showing-ios-apps-bright-natural-window-light-clean-white-workspace-professional-tech-environment-aerial-view-contemporary-design-soft-shadows-high-contrast-background&width=1920&height=800&seq=cta-background-001&orientation=landscape"
          alt="Experience the Difference"
          className="w-full h-full object-cover"
        />
        <div className="absolute inset-0 bg-black/25"></div>
        
        <div className="absolute top-60 left-20">
          <h3 className="text-7xl font-bold text-white leading-tight" style={{ letterSpacing: '-1px' }}>
            experience the<br />
            difference
          </h3>
        </div>
        
        <div className="absolute bottom-72 right-24">
          <p className="text-xl text-white font-light leading-relaxed text-right">
            Professional iOS solutions<br />
            for modern users
          </p>
        </div>
        
        <div className="absolute bottom-24 left-20">
          <div className="bg-white rounded-full px-6 py-3 flex items-center gap-4 shadow-xl">
            <span className="text-gray-800 font-medium">Learn More</span>
            <div className="w-11 h-11 rounded-full bg-gray-900 flex items-center justify-center cursor-pointer hover:bg-gray-800 transition-colors">
              <i className="ri-arrow-right-line text-white"></i>
            </div>
          </div>
        </div>
      </section>

      {/* Contact Section */}
      <section id="contact" className="py-24 bg-emerald-900">
        <div className="max-w-7xl mx-auto px-6">
          <div className="grid grid-cols-1 md:grid-cols-4 gap-12 mb-20">
            {/* Company Info */}
            <div>
              <div className="flex items-center gap-3 mb-6">
                <img 
                  src="https://public.readdy.ai/ai/img_res/56091114-98dd-472c-91c1-3df9f5a8ef47.png" 
                  alt="CHAMPION d.o.o. Logo" 
                  className="h-8 w-8 object-contain"
                />
                <h4 className="text-lg font-semibold text-white">CHAMPION d.o.o.</h4>
              </div>
              <p className="text-sm text-gray-300 leading-relaxed">
                Expert iOS development team creating innovative applications for enhanced mobile experience and security.
              </p>
            </div>

            {/* Contact Form */}
            <div>
              <h5 className="text-xs font-semibold text-gray-400 uppercase tracking-wider mb-6">STAY UPDATED</h5>
              <form data-readdy-form id="contact-form" onSubmit={handleSubmit} className="space-y-4">
                <input
                  type="text"
                  name="name"
                  placeholder="Your Name"
                  value={formData.name}
                  onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                  required
                  className="w-full bg-transparent border-b border-gray-500 text-white placeholder-gray-400 py-2 focus:outline-none focus:border-teal-400 transition-colors text-sm"
                />
                <input
                  type="email"
                  name="email"
                  placeholder="Your Email"
                  value={formData.email}
                  onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                  required
                  className="w-full bg-transparent border-b border-gray-500 text-white placeholder-gray-400 py-2 focus:outline-none focus:border-teal-400 transition-colors text-sm"
                />
                <textarea
                  name="message"
                  placeholder="Your Message"
                  value={formData.message}
                  onChange={(e) => setFormData({ ...formData, message: e.target.value.slice(0, 500) })}
                  required
                  maxLength={500}
                  rows={3}
                  className="w-full bg-transparent border-b border-gray-500 text-white placeholder-gray-400 py-2 focus:outline-none focus:border-teal-400 transition-colors text-sm resize-none"
                />
                <button
                  type="submit"
                  disabled={formStatus === 'submitting'}
                  className="flex items-center gap-2 text-white hover:text-teal-400 transition-colors cursor-pointer whitespace-nowrap text-sm font-medium"
                >
                  {formStatus === 'submitting' ? 'Sending...' : 'Send Message'}
                  <i className="ri-arrow-right-line"></i>
                </button>
                {formStatus === 'success' && (
                  <p className="text-teal-400 text-sm">Message sent successfully!</p>
                )}
                {formStatus === 'error' && (
                  <p className="text-red-400 text-sm">Failed to send message. Please try again.</p>
                )}
              </form>
            </div>

            {/* Quick Links */}
            <div>
              <h5 className="text-xs font-semibold text-gray-400 uppercase tracking-wider mb-6">COMPANY</h5>
              <div className="space-y-4">
                <a href="#about" className="block text-gray-200 hover:text-teal-400 transition-colors cursor-pointer text-sm">About Us</a>
                <a href="#products" className="block text-gray-200 hover:text-teal-400 transition-colors cursor-pointer text-sm">Our Products</a>
                <a href="#contact" className="block text-gray-200 hover:text-teal-400 transition-colors cursor-pointer text-sm">Contact</a>
              </div>
            </div>

            {/* Social Links */}
            <div>
              <h5 className="text-xs font-semibold text-gray-400 uppercase tracking-wider mb-6">CONNECT</h5>
              <div className="space-y-4">
                <div className="text-gray-200 text-sm">LinkedIn</div>
                <div className="text-gray-200 text-sm">Twitter</div>
                <div className="text-gray-200 text-sm">GitHub</div>
              </div>
            </div>
          </div>

          {/* Contact Information & Brand Typography */}
          <div className="relative pt-16">
            <div className="absolute top-0 right-0 text-right">
              <div className="space-y-2 text-sm text-gray-300">
                <div><strong>Address:</strong> V. Nazora 49</div>
                <div><strong>City:</strong> Slatina</div>
                <div><strong>Zip:</strong> 33520</div>
                <div><strong>Country:</strong> Croatia</div>
                <div><strong>Email:</strong> info@championdoo.com</div>
                <div><strong>Number:</strong> +1 7154696019</div>
              </div>
            </div>
            
            <div className="mt-20">
              <h2 className="text-9xl font-bold text-amber-50/20 tracking-tight">CHAMPION</h2>
            </div>
          </div>
        </div>
      </section>

      {/* Footer Bottom */}
      <div className="bg-emerald-950 py-6">
        <div className="max-w-7xl mx-auto px-6">
          <div className="flex flex-col md:flex-row justify-between items-center gap-4 text-sm text-gray-400">
            <p>&copy; 2025 CHAMPION d.o.o. All rights reserved.</p>
            <a 
              href="https://readdy.ai/?ref=logo" 
              target="_blank" 
              rel="noopener noreferrer"
              className="hover:text-teal-400 transition-colors cursor-pointer"
            >
              Powered by Readdy
            </a>
          </div>
        </div>
      </div>
    </div>
  );
}
